import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  createAuthSlice,
  getTokenExpiry as foundationGetTokenExpiry,
  type AuthState as FoundationAuthState,
} from '@foundation-bb/foundation-auth';
import { env } from '@/env';
import type { MeDto } from '@/api/auth/models/MeDto';

// ---------------------------------------------------------------------------
// Cookie-based auth (Foundation pattern). The refresh token lives in an HttpOnly
// cookie the browser sends automatically; JavaScript never reads/stores it.
//
// The access-token slice is built from Foundation's `createAuthSlice`. rys keeps a
// small, app-owned `me` slice alongside it: the rich user profile (display name,
// tenant name, avatar flag, …) comes from `/api/me`, NOT from JWT claims (the
// Foundation backend dropped the `name` claim). `selectIsAuthenticated` is a
// composite of Foundation's token check AND the presence of `me`, so auth-gated
// routes wait for the profile to land before flipping.
//
// The public symbols at the bottom are thin re-export shims so existing
// consumers (LoginPage / SsoCallbackPage / AppShell / api / tests) keep
// compiling against the same import surface as before.
// ---------------------------------------------------------------------------

/**
 * rys's identity decoded from the access token. Display name is intentionally
 * NOT decoded here — there is no `name` claim; it is sourced from `/api/me` via
 * the `me` slice instead.
 */
export interface RysUser {
  id: string;
  email: string;
  role: string;
  tenantId: string;
}

// Exported for direct unit testing of the decode branches (valid payload,
// missing segment, malformed token). It is otherwise wired as `decodeUser`.
export function decodeRysUser(token: string): RysUser {
  const empty: RysUser = { id: '', email: '', role: '', tenantId: '' };
  const segment = token.split('.')[1];
  if (!segment) return empty;
  try {
    const p = JSON.parse(
      atob(segment.replace(/-/g, '+').replace(/_/g, '/')),
    ) as Record<string, unknown>;
    return {
      id: (p.sub as string) ?? '',
      email: (p.email as string) ?? '',
      role: (p.role as string) ?? '',
      tenantId: (p.tid as string) ?? '',
    };
  } catch {
    // A non-JWT or malformed token: identity stays empty. The rich profile comes
    // from /api/me anyway; this decode is only optimistic paint.
    return empty;
  }
}

export const rysAuth = createAuthSlice<RysUser>({
  refreshUrl: `${env.api.baseUrl}/auth/refresh`,
  logoutUrl: `${env.api.baseUrl}/auth/logout`,
  decodeUser: decodeRysUser,
});

// ---------------------------------------------------------------------------
// rys-owned `me` slice — the display-name source (decrypted server-side, served
// by `/api/me`). Kept separate from the Foundation auth slice, which owns only
// the access token + JWT-decoded identity.
// ---------------------------------------------------------------------------

interface MeState {
  me: MeDto | null;
  // True until the FIRST `/me` resolution (success or definitive failure). Without
  // this, a hard refresh flickers the login screen: Foundation's refresh-fulfilled
  // flips `isAuthenticated:true` + `isLoading:false` immediately, but `me` is fetched
  // a beat later — so the composite `isAuthenticated` (which needs `me`) is briefly
  // false while loading is already done → the guard shows login for a frame.
  loading: boolean;
}

const meSlice = createSlice({
  name: 'me',
  initialState: { me: null, loading: true } as MeState,
  reducers: {
    setMe(state, action: PayloadAction<MeDto | null>) {
      state.me = action.payload;
      state.loading = false;
    },
    clearMe(state) {
      state.me = null;
      state.loading = false;
    },
  },
});

export const meReducer = meSlice.reducer;
export const { setMe, clearMe } = meSlice.actions;

// ---------------------------------------------------------------------------
// Reducer wired into the store under `auth`.
// ---------------------------------------------------------------------------

export const authReducer = rysAuth.reducer;

// State shape this module's selectors read against.
export interface AuthRootSlice {
  auth: FoundationAuthState<RysUser>;
  me: MeState;
}

// ---------------------------------------------------------------------------
// Foundation pass-throughs.
// ---------------------------------------------------------------------------

export const { applyTokens, setAuthLoading, clearAuth } = rysAuth.actions;
export const { refreshToken, logoutUser } = rysAuth.thunks;
export const tokenReceived = rysAuth.tokenReceived;
export const getTokenExpiry = foundationGetTokenExpiry;

// ---------------------------------------------------------------------------
// Compat shims — keep existing consumers compiling unchanged.
// ---------------------------------------------------------------------------

/** Apply tokens after login/SSO/verify-code. Only the access token is stored
 *  (Foundation derives identity + expiry from the JWT). */
export function setAuth(payload: { accessToken: string }) {
  return rysAuth.tokenReceived({ accessToken: payload.accessToken });
}

/** Clear both the access token and the loaded profile (sign-out / failed refresh). */
export function clear() {
  // Returned as a thunk-style payload so call sites can `dispatch(clear())`.
  return (dispatch: (a: unknown) => void) => {
    dispatch(rysAuth.actions.clearAuth());
    dispatch(meSlice.actions.clearMe());
  };
}

export const selectAccessToken = rysAuth.selectors.selectAccessToken;
export const selectAuthUser = rysAuth.selectors.selectAuthUser;
export const selectMe = (s: { me: MeState }) => s.me.me;

/**
 * Loading iff Foundation is still bootstrapping the token, OR a token came back
 * but the `/me` profile that `selectIsAuthenticated` depends on hasn't landed yet.
 * The second clause closes the hard-refresh flicker window (token authenticated,
 * profile not yet fetched) so guards show the spinner instead of the login screen.
 */
export const selectIsLoading = (s: AuthRootSlice) =>
  rysAuth.selectors.selectIsAuthLoading(s) ||
  (rysAuth.selectors.selectIsAuthenticated(s) && s.me.loading);

/** Authenticated iff there's a valid access token AND the `/me` profile is loaded. */
export const selectIsAuthenticated = (s: AuthRootSlice) =>
  rysAuth.selectors.selectIsAuthenticated(s) && !!s.me.me;
